// detectWebsiteFeatures — inspect a plain website URL and discern two things the chat onboarding
// needs to know before it can set QA up:
//   a) is the site PUBLICLY ACCESSIBLE (reachable from the public internet), or is it a
//      localhost / private-network URL that will need a reverse-proxy tunnel first, and
//   b) is the site PROTECTED BY A LOGIN (an auth wall the agent must get past — with credentials
//      the user provides, or by self-registering a test account).
//
// The detection is a single bounded, best-effort HTTP probe run server-side (a browser can't do
// this — CORS blocks reading a cross-origin response, and it can't reach a private host anyway).
// Everything here is heuristic and non-fatal: when we can't tell we never BLOCK onboarding, but we
// distinguish "confirmed open" from "couldn't tell" via loginCertainty so the chat can narrate
// honestly instead of falsely promising a login-free site (see CHATQA-1). That non-fatal rule
// covers certificate validation failures too: a host whose certificate fails validation
// answered on its public port, so it is a reachable public site with an unknown login verdict —
// never a local-computer app that needs a reverse-proxy tunnel.

import { isPrivateHost } from '../../../src/guidance/lib/http-target-url.ts'
// Shared with the browse runners — write-time project/journey asserts and runtime navigate guards.
export {
  isPrivateHost,
  assertHttpTargetUrl,
  type HttpTargetUrlOptions,
} from '../../../src/guidance/lib/http-target-url.ts'

/** How sure we are about the login verdict:
 *  - 'confirmed': we saw a hard auth signal (401/403, a redirect to sign-in, an auth-y entrypoint
 *    path, or a login form in the served HTML).
 *  - 'none': we fetched real content and saw no login signal — reasonably sure it's open.
 *  - 'unknown': we couldn't tell (a client-rendered SPA shell we can't inspect, a 5xx, or a failed
 *    probe). Routing treats this like 'none' (don't block onboarding), but the chat must NOT claim
 *    the site is open — it narrates an honest "couldn't fully check" instead. */
export type LoginCertainty = 'confirmed' | 'none' | 'unknown'

export interface WebsiteFeatures {
  /** The normalized URL that was probed (https:// by default, or http:// for bare localhost). */
  url: string
  /** Reachable from the public internet. False for localhost / private-network / unreachable hosts. */
  publiclyAccessible: boolean
  /** The site appears to sit behind a login / auth wall. Kept as a boolean for back-compat; it is
   *  exactly `loginCertainty === 'confirmed'`. Prefer `loginCertainty` for new logic. */
  loginProtected: boolean
  /** Confidence in the login verdict — see LoginCertainty. */
  loginCertainty: LoginCertainty
  /** Short machine-readable note on how we concluded — for logging / debugging, not user-facing. */
  reason: 'ok' | 'login' | 'uncertain' | 'private-host' | 'unreachable' | 'invalid-url'
  /** HTTP status of the probe response, when we got one. */
  statusCode?: number
}

const PROBE_TIMEOUT_MS = 8000
const MAX_BODY_BYTES = 256 * 1024 // enough to see the <head> + a login form; don't slurp huge pages

const URL_SCHEME_RE = /^[a-z][a-z0-9+.-]*:\/\//i
const BARE_LOCALHOST_RE = /^localhost(?::\d+)?(?:[/?#]|$)/i

// Add a scheme so `new URL` parses a bare "example.com". Local development servers commonly use
// plain HTTP, so a bare localhost URL must not be upgraded to HTTPS before the frpc flow sees it.
export function normalizeUrl(raw: string): string {
  const trimmed = raw.trim()
  if (!trimmed) return ''
  if (URL_SCHEME_RE.test(trimmed)) return trimmed
  return `${BARE_LOCALHOST_RE.test(trimmed) ? 'http' : 'https'}://${trimmed}`
}

// URL paths / redirect targets that clearly point at an authentication surface.
const LOGIN_URL_RE = /\b(log[-_]?in|sign[-_]?in|signin|login|sso|oauth|auth(?:enticate|orize)?|account\/login)\b/i

// Markers in a landing page's HTML that indicate a login form is the primary content.
const PASSWORD_FIELD_RE = /<input\b[^>]*\btype\s*=\s*["']?password["']?/i
const LOGIN_TEXT_RE = /\b(sign in|log in|sign-in|log-in|password)\b/i

function looksLikeLoginRedirect(location: string | null, baseUrl: string): boolean {
  if (!location) return false
  try {
    // Resolve relative Location values against the probed URL.
    const abs = new URL(location, baseUrl)
    return LOGIN_URL_RE.test(abs.pathname) || LOGIN_URL_RE.test(abs.search)
  } catch {
    return LOGIN_URL_RE.test(location)
  }
}

// The probed URL's OWN path/query points at an authentication surface (e.g. an entrypoint of
// ".../login?redirect=/index"). A strong, cheap login signal the HTML heuristics can't see.
function urlLooksAuthy(parsed: URL): boolean {
  return LOGIN_URL_RE.test(parsed.pathname) || LOGIN_URL_RE.test(parsed.search)
}

function decodeUserInfo(value: string): string {
  try { return decodeURIComponent(value) } catch { return value }
}

// Fetch implementations reject URLs containing userinfo, while browsers use it to send HTTP Basic
// Auth. Probe the same origin without embedding the secret in the request URL and carry the credentials
// in the standard Authorization header instead; the browser navigation later still receives the original
// URL and handles the credentials itself.
function probeRequest(parsed: URL): { url: string; authorization?: string } {
  if (!parsed.username && !parsed.password) return { url: parsed.href }
  const probeUrl = new URL(parsed.href)
  probeUrl.username = ''
  probeUrl.password = ''
  const credentials = `${decodeUserInfo(parsed.username)}:${decodeUserInfo(parsed.password)}`
  const bytes = new TextEncoder().encode(credentials)
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return { url: probeUrl.href, authorization: `Basic ${btoa(binary)}` }
}

// Markup that is in the served HTML but is not the app rendering: the preview badge a hosting
// platform appends to every deploy (Lovable's "Edit with Lovable" and its ilk), <noscript>
// fallbacks, and inert <template> content. None of it is evidence the app painted anything.
const NON_APP_CHROME_RES = [
  /<aside\b[^>]*\bid\s*=\s*["']?[\w-]*badge[\w-]*["']?[^>]*>[\s\S]*?<\/aside\s*>/gi,
  /<noscript\b[\s\S]*?<\/noscript\s*>/gi,
  /<template\b[\s\S]*?<\/template\s*>/gi,
]

// Whether a probe rejection is a TLS CERTIFICATE VALIDATION failure rather than a transport
// failure. fetch (undici) rejects with a TypeError whose `cause` carries the driver error and its
// machine code: certificate validation failures carry the OpenSSL verify codes and Node's
// ERR_TLS_CERT_* family ('CERT_HAS_EXPIRED', 'SELF_SIGNED_CERT_IN_CHAIN',
// 'DEPTH_ZERO_SELF_SIGNED_CERT', 'UNABLE_TO_GET_ISSUER_CERT_LOCALLY',
// 'UNABLE_TO_VERIFY_LEAF_SIGNATURE', 'ERR_TLS_CERT_ALTNAME_INVALID', …), while transport failures
// carry different codes ('ENOTFOUND', 'ECONNREFUSED', 'ETIMEDOUT', 'ECONNRESET',
// 'UND_ERR_CONNECT_TIMEOUT', …) or none at all. The distinction is load-bearing for reachability:
// a certificate that fails validation still proves the host answered on a public port, so the
// site is NOT a localhost / private-network app that needs a reverse-proxy tunnel.
function isCertificateValidationFailure(error: unknown): boolean {
  const cause = (error as { cause?: unknown } | null | undefined)?.cause
  const code = (cause as { code?: unknown } | null | undefined)?.code
  return typeof code === 'string' && /(^|_)CERT|^ERR_TLS_CERT/i.test(code)
}


// A spinner / skeleton / busy marker in the served body: the app is mid-boot, so whatever it paints
// next — a sign-in wall included — is invisible to this probe.
const LOADING_INDICATOR_RE =
  /\b(?:class|id)\s*=\s*["'][^"']*\b(?:animate-spin|spinner|loader|skeleton)\b|\baria-busy\s*=\s*["']?true|\brole\s*=\s*["']?progressbar/i

// Isolate what a browser would actually render — the <body>. <head> text (above all a descriptive
// <title>) is never on screen, so it must not count as content below.
function bodyHtml(html: string): string {
  const open = html.match(/<body\b[^>]*>/i)
  if (open?.index !== undefined) {
    const rest = html.slice(open.index + open[0].length)
    const close = rest.search(/<\/body\s*>/i)
    return close === -1 ? rest : rest.slice(0, close)
  }
  // No <body> tag (a fragment, or malformed markup) — drop <head> and judge the remainder.
  return html.replace(/<head\b[\s\S]*?<\/head\s*>/i, ' ')
}

// A client-rendered SPA serves a near-empty JS shell (a mount <div> + <script> tags) and paints its
// real UI — including any login form — in the browser. Our lightweight HTTP probe can't run that JS,
// so it can't see a login form that isn't in the served HTML. When the body is such a shell we must
// report 'unknown' rather than falsely asserting the site is open.
//
// Measuring the WHOLE document's text used to defeat this: a real app's <title> plus the host's
// preview badge clear the budget on their own, so every SPA with a descriptive title read as real
// content and got a confident "doesn't require a login" (CHATQA-1, round two). Judge the body only,
// with non-app chrome removed. Erring toward 'unknown' is the safe direction — it just makes chat
// ask about the login instead of promising there isn't one.
function looksLikeEmptyShell(html: string): boolean {
  if (!/<script\b/i.test(html)) return false
  let body = bodyHtml(html)
  for (const re of NON_APP_CHROME_RES) body = body.replace(re, ' ')
  if (LOADING_INDICATOR_RE.test(body)) return true
  const visibleText = body
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<svg[\s\S]*?<\/svg>/gi, ' ')
    .replace(/<!--[\s\S]*?-->/g, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  return visibleText.length < 40
}

// Read at most MAX_BODY_BYTES of the response body as text (bounded so a huge page can't OOM us).
async function readBoundedText(res: Response): Promise<string> {
  if (!res.body) {
    try { return (await res.text()).slice(0, MAX_BODY_BYTES) } catch { return '' }
  }
  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let out = ''
  try {
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      if (value) out += decoder.decode(value, { stream: true })
      if (out.length >= MAX_BODY_BYTES) break
    }
  } catch {
    /* partial body is fine for our heuristics */
  } finally {
    try { await reader.cancel() } catch { /* ignore */ }
  }
  return out.slice(0, MAX_BODY_BYTES)
}

/**
 * Probe `rawUrl` and report whether it's publicly accessible and whether it's behind a login.
 * `fetchImpl` is injectable for testing; defaults to the global fetch.
 */
export async function detectWebsiteFeatures(
  rawUrl: string,
  fetchImpl: typeof fetch = fetch,
): Promise<WebsiteFeatures> {
  const url = normalizeUrl(rawUrl)
  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    return feature(url, false, 'none', 'invalid-url')
  }

  // Private / non-routable hosts can't be probed and always need a reverse proxy.
  if (isPrivateHost(parsed.hostname)) {
    return feature(url, false, 'none', 'private-host')
  }

  // The entrypoint's own path pointing at a sign-in surface is a strong login signal on its own,
  // regardless of what the probe returns below.
  const pathAuthy = urlLooksAuthy(parsed)
  const request = probeRequest(parsed)

  let res: Response
  try {
    res = await fetchImpl(request.url, {
      method: 'GET',
      // Manual redirects so a 3xx to a sign-in page reads as "login protected" rather than being
      // silently followed. Send a browser-y UA so bot-walls don't skew the result.
      redirect: 'manual',
      headers: {
        'User-Agent': 'Mozilla/5.0 (compatible; ReplayQA-Onboarding/1.0)',
        Accept: 'text/html,*/*',
        ...(request.authorization ? { Authorization: request.authorization } : {}),
      },
      signal: AbortSignal.timeout(PROBE_TIMEOUT_MS),
    })
  } catch (error) {
    // A TLS certificate validation failure (expired, self-signed, untrusted or
    // hostname-mismatched certificate) is NOT an unreachable host: the server answered on its
    // public port and only the certificate failed validation. Reporting it as not publicly
    // accessible would mislabel a public site as a local-computer app and dead-end onboarding on
    // the reverse-proxy connection card — a tunnel to the user's computer can never serve a
    // third-party public site. Classify it as reachable with unknown certainty instead: the chat
    // honestly asks the login question (skippable, never blocking) and onboarding proceeds. The
    // entrypoint path's own auth signal survives the failed probe exactly as it does on a 5xx.
    if (isCertificateValidationFailure(error)) {
      return feature(url, true, pathAuthy ? 'confirmed' : 'unknown', pathAuthy ? 'login' : 'uncertain')
    }
    // DNS failure, connection refused, timeout — not reachable from here.
    return feature(url, false, 'none', 'unreachable')
  }

  const status = res.status

  // A hard auth challenge is the clearest login signal.
  if (status === 401 || status === 403) {
    return feature(url, true, 'confirmed', 'login', status)
  }

  // A redirect to a sign-in surface (or an already-auth-y entrypoint path).
  if (status >= 300 && status < 400) {
    const confirmed = pathAuthy || looksLikeLoginRedirect(res.headers.get('location'), url)
    return feature(url, true, confirmed ? 'confirmed' : 'none', confirmed ? 'login' : 'ok', status)
  }

  // Otherwise inspect the landing page for a login form.
  if (status >= 200 && status < 300) {
    const html = await readBoundedText(res)
    const htmlHasLogin = PASSWORD_FIELD_RE.test(html) || (LOGIN_TEXT_RE.test(html) && LOGIN_URL_RE.test(html))
    if (pathAuthy || htmlHasLogin) return feature(url, true, 'confirmed', 'login', status)
    // A client-rendered shell hides its login form from the probe — don't assert the site is open.
    if (looksLikeEmptyShell(html)) return feature(url, true, 'unknown', 'uncertain', status)
    return feature(url, true, 'none', 'ok', status)
  }

  // Any other status (e.g. 5xx) — reachable, but we can't tell about auth.
  return feature(url, true, pathAuthy ? 'confirmed' : 'unknown', pathAuthy ? 'login' : 'uncertain', status)
}

// Assemble a WebsiteFeatures result, deriving the back-compat `loginProtected` boolean from certainty.
function feature(
  url: string,
  publiclyAccessible: boolean,
  loginCertainty: LoginCertainty,
  reason: WebsiteFeatures['reason'],
  statusCode?: number,
): WebsiteFeatures {
  return { url, publiclyAccessible, loginProtected: loginCertainty === 'confirmed', loginCertainty, reason, statusCode }
}
